<template>
  <eg-b-overlay v-if="subscriptionLoading" :show="subscriptionLoading" no-wrap>
    <template #overlay>
      <eg-b-spinner small variant="info" />
    </template>
  </eg-b-overlay>
  <div v-else class="subscription-content">
    <div v-if="isUserSubscribed" class="subscription-content-cards">
      <eg-card class="subscription-content-card">
        <div class="subscription-content-header">
          <div class="d-flex justify-content-between align-items-center">
            <div class="paragraph-m semi-bold">
              Current Plan: {{ capitalizeEachWord(user.plan) }}
            </div>

            <div v-if="user.stripeSubscriptionId && !basicPlan">
              <span class="heading-s semi-bold">
                ${{ accountProvider.currentSubscriptionAmount }}
              </span>
              <span> /{{ accountProvider.paymentInterval }} </span>
            </div>
          </div>

          <div
            v-if="showSubscriptionActions && accountProvider.subscription"
            class="subscription-nextBillingDate"
          >
            <eg-icon icon="calendar-date" />

            <span v-if="scheduledToCancel" class="paragraph-s">
              Canceled on {{ cancelAtDate }}
            </span>
            <span v-else-if="isPaused" class="paragraph-s">
              Paused until {{ pausedUntil }}
            </span>
            <span v-else class="paragraph-s">
              Renews {{ formatedNextBillingDate }}
            </span>
          </div>
        </div>

        <div class="essays-container">
          <eg-progress-bar
            :max="accountProvider.usage?.essaysUsage?.limit"
            :value="accountProvider.usage?.essaysUsage?.count"
            label="Essays"
          />
        </div>

        <div v-if="showSubscriptionActions" class="divider"></div>

        <div v-if="showSubscriptionActions" class="subscription-content-footer">
          <eg-button
            v-if="!scheduledToCancel && !isPaused"
            type="ordinary"
            @click="handleCancelSubscription"
          >
            {{
              scheduledToCancel ? "Renew Subscription" : "Cancel Subscription"
            }}
          </eg-button>
          <eg-button
            v-if="!scheduledToCancel && !isYearlyPlan"
            type="ordinary"
            @click="handlePauseUnpauseSubscription"
          >
            {{ isPaused ? "Unpause Subscription" : "Pause Subscription" }}
          </eg-button>
          <eg-button
            type="secondary"
            :is-loading="loading"
            @click="createPortalSession"
          >
            Manage Subscription
          </eg-button>
        </div>
      </eg-card>

      <eg-card
        v-if="showSubscriptionActions && nextPlanCard"
        class="next-plan-card"
      >
        <div class="d-flex">
          <div class="d-flex flex-column flex-2 gap-1">
            <div class="paragraph-m semi-bold">
              {{ capitalizeEachWord(nextPlanCard.name) }}
            </div>

            <div class="subscription-nextBillingDate">
              <span class="paragraph-s color-text-on-surface-secondary">
                {{ nextPlanCard.description }}
              </span>
            </div>
          </div>

          <div class="d-flex justify-content-end flex-3">
            <span class="heading-s semi-bold">
              ${{
                accountProvider.paymentInterval === "year"
                  ? nextPlanCard.yearlyPrice
                  : nextPlanCard.monthlyPrice
              }}
            </span>
            <span> /{{ accountProvider.paymentInterval }} </span>
          </div>
        </div>

        <div class="d-flex gap-4">
          <div
            v-for="feature in nextPlanCard.features.slice(0, 2)"
            :key="feature"
            class="d-flex align-items-center gap-2"
          >
            <eg-icon icon="check-upgrade" />
            <span class="caption-s semi-bold">
              {{ feature }}
            </span>
          </div>
        </div>

        <div class="d-flex gap-3">
          <eg-button
            class="glass-button"
            type="primary"
            icon="stars"
            :is-loading="upgradeLoading"
            @click="upgradePlan"
          >
            Upgrade Plan
          </eg-button>

          <eg-button
            class="compare-plans-btn"
            type="primary"
            @click="comparePlans"
          >
            Compare Plans
          </eg-button>
        </div>
      </eg-card>
    </div>

    <div v-else class="d-flex flex-column gap-6">
      <div class="d-flex align-items-center justify-content-end gap-2">
        <div class="paragraph-s color-text-neutral-fade">
          {{ $t("account.subscription.monthly") }}
        </div>

        <toggle-button
          v-model="yearlyToggle"
          :labels="false"
          color="#8A3FFC"
          :width="40"
          :height="22"
          :disabled="isYearlyPlan"
        />

        <div class="d-flex align-items-center gap-2">
          <div class="paragraph-s medium">
            {{ $t("account.subscription.annually") }}
          </div>
          <!-- <div class="discount-badge caption-xs">
            {{ discountPercent }}% OFF
          </div>-->
        </div>
      </div>

      <pricing-cards
        :current-plan="accountProvider.subscription?.plan"
        :yearly-toggle="yearlyToggle"
      />
    </div>
  </div>
</template>

<script>
import EgCard from "@/components/global/eg_card.vue";
import EgProgressBar from "@/components/global/eg/eg_progress_bar.vue";
import PricingCards from "../../components/account/pricing-cards.vue";
import { ToggleButton } from "vue-js-toggle-button";
import { LITE_PLAN } from "@/constants";
import { capitalizeEachWord, capitalize } from "@/utils/capitalize";
import UpgradePlanModal from "@/components/global/modals/upgrade_plan_modal.vue";
import { customerPortal, applyCoupon, cancel, pause, unpause } from "@/api";
import upgradePlanMixin from "@/mixins/upgradePlan";
import formatDate from "@/utils/formatDate";
import CancelSubscriptionModal from "@/components/account/subscription/cancel_subscription_modal.vue";
import PauseSubscriptionModal from "@/components/account/subscription/pause_subscription_modal.vue";
import { mergeUserData } from "@/utils/userUtils";

export default {
  name: "SubscriptionContent",

  components: {
    EgCard,
    EgProgressBar,
    PricingCards,
    ToggleButton,
  },

  mixins: [
    /*
    this.error
    this.upgradeWithConfirmation
    this.upgradeCurrentPaidPlan
    this.upgradeLoading
    */
    upgradePlanMixin,
  ],

  inject: ["accountProvider"],

  data() {
    return {
      loading: false,
      yearlyToggle: true,
      discountPercent: 30,
    };
  },

  computed: {
    user() {
      return this.$store.state.user || {};
    },

    formatedNextBillingDate() {
      return formatDate(this.accountProvider.nextBillingDate);
    },

    nextPlanCard() {
      switch (this.user.plan) {
        case "basic":
          return this.accountProvider.availablePlans["lite"];

        case "lite":
          return this.accountProvider.availablePlans["pro"];

        case "pro":
          return this.accountProvider.availablePlans["premium"];

        case "premium":
          return null;

        case "district":
          return null;

        default:
          return this.accountProvider.availablePlans["premium"];
      }
    },

    subscriptionLoading() {
      return this.accountProvider.subscriptionLoading;
    },

    isUserSubscribed() {
      return this.accountProvider.isUserSubscribed;
    },

    showSubscriptionActions() {
      return this.accountProvider.showSubscriptionActions;
    },

    basicPlan() {
      return this.$store.getters.basicPlan;
    },

    isPaused() {
      return this.$store.getters.isPaused;
    },

    scheduledToCancel() {
      return this.accountProvider.subscription?.cancel_at_period_end;
    },

    cancelAtDate() {
      if (this.scheduledToCancel) {
        return formatDate(
          new Date(this.accountProvider.subscription?.cancel_at * 1000),
          "YYYY, MMM D, h:mm A"
        );
      }
      return "";
    },

    pausedUntil() {
      return this.isPaused ? formatDate(this.user.pausedUntil) : "";
    },

    isYearlyPlan() {
      return this.accountProvider.paymentInterval === "year";
    },
  },

  methods: {
    capitalizeEachWord,

    handlePauseUnpauseSubscription() {
      if (this.isPaused) {
        this.resumeSubscription();
      } else {
        this.openPauseSubscriptionModal();
      }
    },

    handleCancelSubscription() {
      if (this.scheduledToCancel) {
        // this.renewSubscription();
      } else {
        this.openCancelSubscriptionModal();
      }
    },

    async createPortalSession() {
      try {
        this.loading = true;
        const session = await customerPortal();
        // NOTE: this is not needed because we redirect user to external page
        // this.loading = false;

        window.location.href = session.data.url;
      } catch (error) {
        this.$showToastError(
          error.response?.data?.error ||
            error?.message ||
            "Error getting a customer portal session, please try again or contact support."
        );
      }
    },

    upgradePlan() {
      // For existing subscribers, pass the current yearly toggle state
      // For new subscribers, pass the toggle value from the UI
      const yearlyToggleValue = this.isUserSubscribed
        ? this.isYearlyPlan // Preserve current billing interval for existing subscribers
        : this.yearlyToggle; // Use toggle value for new subscribers

      this.upgradeWithConfirmation({
        nextPlan: this.nextPlanCard,
        yearlyToggle: yearlyToggleValue,
      });
    },

    comparePlans() {
      this.$egModal.show("upgrade-plan-modal", {
        component: UpgradePlanModal,
        props: {
          currentPlan: this.accountProvider.subscription?.plan,
        },
      });
    },

    openPauseSubscriptionModal() {
      this.$egModal.show("pause_subscription_modal", {
        component: PauseSubscriptionModal,
        context: this,
        propsKeys: ["pauseSubscription", "loading"],
      });
    },

    openCancelSubscriptionModal() {
      const username = `${capitalize(this.user?.firstName || "")} ${capitalize(
        this.user?.lastName || ""
      )}`;
      this.$egModal.show("cancel_subscription_modal", {
        component: CancelSubscriptionModal,
        context: this,
        propsKeys: [
          "cancelSubscription",
          "pauseSubscription",
          "loading",
          "applyCoupon",
        ],
        props: {
          switchPlan: async () => {
            await this.upgradeCurrentPaidPlan({
              planText: "lite",
              yearlyToggle: this.isYearlyPlan, // Preserve current billing interval
              isDowngrade: true,
            });
            this.$egModal.hide("cancel_subscription_modal");
          },
          username: username,
          isLitePlan: this.user.plan === LITE_PLAN,
          parentContext: this,
        },
      });
    },

    async resumeSubscription() {
      try {
        this.loading = true;

        await unpause(this);

        this.loading = false;

        this.$store.dispatch("setUser", {
          ...mergeUserData(this, this.user),
          isPaused: false,
        });
      } catch (error) {
        this.$showToastError(
          error.response?.data?.error ||
            error?.message ||
            "There was a problem resuming your subscription, please try again or contact support."
        );
        this.loading = false;
      }
    },
    async pauseSubscription(pauseForMonths) {
      try {
        this.loading = true;
        const { data } = await pause({ pauseForMonths });
        this.loading = false;
        const { pausedUntil } = data;
        this.$store.dispatch("setUser", {
          ...mergeUserData(this, {
            ...this.user,
            isPaused: true,
            pausedUntil,
          }),
        });
        this.$showToastSuccess("Your plan was successfully paused.", {
          title: "Success!",
        });

        return true;
      } catch (error) {
        this.loading = false;
        this.$showToastError(
          error.response?.data?.error ||
            error?.message ||
            "There was a problem processing your request, please try again or contact support.",
          {
            title: "Error!",
          }
        );

        return false;
      }
    },
    async cancelSubscription(reasons) {
      try {
        this.loading = true;

        const response = await cancel(reasons);

        this.loading = false;
        // TODO: update the subscription in the account provider, change provide/inject bad practice
        this.accountProvider.subscription = {
          ...this.accountProvider.subscription,
          cancel_at_period_end:
            this.accountProvider.subscription.current_period_end,
          cancel_at: response.data.cancel_at,
        };

        this.$egModal.hide("cancel_subscription_modal");
      } catch (error) {
        this.$showToastError(
          error.response?.data?.error ||
            error?.message ||
            "There was a problem canceling your subscription, please try again or contact support."
        );
        this.loading = false;
      }
    },
    async applyCoupon(coupon = "3-month-discount") {
      try {
        this.loading = true;
        await applyCoupon(coupon);
        await this.initSubscriptionData();
        this.loading = false;
        this.error = "";
      } catch (error) {
        this.error = error.response?.data?.error;
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.divider {
  width: 100%;
  height: 1px;
  background-color: var(--color-stroke-neutral-default);
}

.subscription-content {
  label.vue-js-switch {
    margin: 0 !important;
  }
}
.subscription-content-cards {
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
}

@media (max-width: 1366px) {
  .subscription-content-cards {
    flex-wrap: wrap;
  }
}

.subscription-content-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.essays-container {
  border: 1px solid var(--color-stroke-neutral-default);
  border-radius: 8px;
  padding: 16px;
}

.subscription-content-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subscription-nextBillingDate {
  display: flex;
  align-items: center;
  gap: 4px;
}

.subscription-content-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.next-plan-card {
  background: radial-gradient(
      90.66% 90.66% at 50% 9.34%,
      #6a50f3 0%,
      rgba(62, 46, 141, 0.53) 100%
    ),
    linear-gradient(
      0deg,
      var(--colors-purple-500, #8a3ffc),
      var(--colors-purple-500, #8a3ffc)
    );
  color: var(--color-text-on-surface-default);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
}

.color-text-on-surface-secondary {
  color: var(--color-text-on-surface-secondary);
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 24px;
}

.gap-6 {
  gap: 40px;
}

.flex-2 {
  flex: 2;
}

.flex-3 {
  flex: 3;
}

.glass-button {
  background-color: #f6f2ff1a !important;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08);
  overflow: hidden !important;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      transparent 100%
    );
  }
}

.compare-plans-btn {
  border: 1px #e5ebf0 solid !important;
  background-color: transparent !important;
}
</style>
